/**
 * Scroll spy — marks in-page links while their section is in view.
 *
 * Every same-page `#id` link whose target exists (the links scroll-manager.js
 * smooth-scrolls) gets `.is-active` and `aria-current="location"`, and its menu
 * `<li>` `.is-active`, while its target is the current section: the last target
 * (in document order, so a nested target beats its container) that spans the
 * activation line — 40% down the viewport, never above the header's bottom
 * edge. At the end of the page the last target in view is current, so a short
 * final section still activates. Nothing to configure: links are found on each
 * page load. `#`, `#top` and links inside `[data-scroll-ignore]` or the admin
 * bar are skipped.
 */
import { isSamePageUrl } from '../core/scroll-manager.js';

const ACTIVE_CLASS = 'is-active';
const LINE_RATIO = 0.4;

let state = null;

/**
 * Same-page link targets on the page, each with its links, in document order.
 * @returns {Map<Element, HTMLAnchorElement[]>}
 */
function collectTargets() {
    const groups = new Map();

    document.querySelectorAll('a[href*="#"]').forEach((link) => {
        if (link.closest('[data-scroll-ignore], #wpadminbar')) return;

        const url = new URL(link.href, window.location.href);
        if (!isSamePageUrl(url) || url.hash.length < 2 || url.hash.toLowerCase() === '#top') return;

        let id;
        try {
            id = decodeURIComponent(url.hash.slice(1));
        } catch (e) {
            return;
        }
        const target = document.getElementById(id);
        if (!target) return;

        if (!groups.has(target)) groups.set(target, []);
        groups.get(target).push(link);
    });

    const ordered = [...groups.keys()].sort((a, b) =>
        (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1
    );
    return new Map(ordered.map((target) => [target, groups.get(target)]));
}

/**
 * The target that is the current section, or null (e.g. above the first one).
 * @param {Element[]} targets In document order.
 * @returns {Element|null}
 */
function currentTarget(targets) {
    const header = document.getElementById('header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const line = Math.max(headerBottom + 1, window.innerHeight * LINE_RATIO);
    const root = document.documentElement;
    const atEnd = window.innerHeight + window.scrollY >= root.scrollHeight - 2;

    let current = null;
    for (const target of targets) {
        const rect = target.getBoundingClientRect();
        if (!rect.height) continue; // Hidden.
        if (atEnd ? (rect.top < window.innerHeight && rect.bottom > headerBottom) : (rect.top <= line && rect.bottom > line)) {
            current = target;
        }
    }
    return current;
}

/**
 * Mark or unmark a target's links.
 * @param {HTMLAnchorElement[]} links
 * @param {boolean} active
 */
function mark(links, active) {
    links.forEach((link) => {
        link.classList.toggle(ACTIVE_CLASS, active);
        if (active) {
            link.setAttribute('aria-current', 'location');
        } else if (link.getAttribute('aria-current') === 'location') {
            link.removeAttribute('aria-current');
        }
        link.closest('li.menu-item')?.classList.toggle(ACTIVE_CLASS, active);
    });
}

/**
 * Start watching the page's same-page links.
 * @returns {void}
 */
export function initializeScrollSpy() {
    destroyScrollSpy();

    const groups = collectTargets();
    if (!groups.size) return;

    const targets = [...groups.keys()];
    state = { groups, active: null, frame: 0, observer: null };

    const update = () => {
        state.frame = 0;
        const next = currentTarget(targets);
        if (next === state.active) return;
        if (state.active) mark(state.groups.get(state.active), false);
        if (next) mark(state.groups.get(next), true);
        state.active = next;
    };

    state.schedule = () => {
        if (!state.frame) state.frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', state.schedule, { passive: true });
    window.addEventListener('resize', state.schedule);
    // Late images, accordions etc. move sections without scrolling.
    state.observer = new ResizeObserver(state.schedule);
    state.observer.observe(document.body);

    update();
}

/**
 * Stop watching and clear every mark.
 * @returns {void}
 */
export function destroyScrollSpy() {
    if (!state) return;

    window.removeEventListener('scroll', state.schedule);
    window.removeEventListener('resize', state.schedule);
    state.observer?.disconnect();
    if (state.frame) cancelAnimationFrame(state.frame);
    if (state.active) mark(state.groups.get(state.active), false);
    state = null;
}
